/**
 * 前端声调工具
 * ============================================================
 * 后端 utils/jyutping.js 是 CommonJS 且位于另一个工程根目录，
 * 前端（ESM + Vite）不适合直接跨过去引用，因此这里放一份前端所需的子集。
 *
 * ⚠️ 防漂移：_smoke/virtualtutor.smoke.jsx 里有一条一致性测试，
 * 会同时加载本模块与后端 utils/jyutping.js，逐项比对声调名称表。
 * 如果哪天有人只改了一边，测试会失败。
 */

/** 粤拼声调编号 → 中文调名（1-6 舒声，7-9 入声） */
export const TONE_NAMES = {
  1: '陰平',
  2: '陰上',
  3: '陰去',
  4: '陽平',
  5: '陽上',
  6: '陽去',
  7: '陰入',
  8: '中入',
  9: '陽入',
};

/** 声调调值走向的通俗说明 */
export const TONE_CONTOURS = {
  1: '高平',
  2: '中升',
  3: '中平',
  4: '低降',
  5: '低升',
  6: '低平',
  7: '高短促',
  8: '中短促',
  9: '低短促',
};

/**
 * 取声调中文名
 * @param {number|null} tone
 * @returns {string}
 */
export function getToneName(tone) {
  return TONE_NAMES[tone] || '未知';
}

/**
 * 描述声调读法，例如「陰上（中升）」
 * @param {number|null} tone
 * @returns {string}
 */
export function describeTone(tone) {
  if (!tone || !TONE_NAMES[tone]) return '';
  return `${TONE_NAMES[tone]}（${TONE_CONTOURS[tone] || ''}）`;
}

/**
 * 声调准确度 → 配色档位
 * @param {number} accuracy - 0..100
 * @returns {'good'|'fair'|'poor'|'unknown'}
 */
export function gradeToneAccuracy(accuracy) {
  if (typeof accuracy !== 'number' || Number.isNaN(accuracy)) return 'unknown';
  if (accuracy >= 80) return 'good';
  if (accuracy >= 60) return 'fair';
  return 'poor';
}

/** 配色档位 → Tailwind 类名（浅色/深色两套） */
export const GRADE_CLASSES = {
  good: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/50 dark:text-emerald-200 dark:border-emerald-700',
  fair: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/50 dark:text-amber-200 dark:border-amber-700',
  poor: 'bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-900/50 dark:text-rose-200 dark:border-rose-700',
  unknown: 'bg-zinc-100 text-zinc-600 border-zinc-200 dark:bg-zinc-700/60 dark:text-zinc-300 dark:border-zinc-600',
};

/** 配色档位 → 中文评语 */
export const GRADE_LABELS = {
  good: '声调准确',
  fair: '声调基本正确',
  poor: '声调需调整',
  unknown: '未评测',
};

/**
 * 汇总逐音节结果，用于给出一句总评
 *
 * @param {Array} syllables - detectedTones 数组
 * @returns {{ total: number, poor: number, fair: number, good: number, compared: number, worst: Array }}
 */
export function summarizeSyllables(syllables) {
  const list = Array.isArray(syllables) ? syllables : [];
  const result = { total: list.length, poor: 0, fair: 0, good: 0, compared: 0, worst: [] };

  list.forEach((s) => {
    if (!s || typeof s !== 'object') return;
    if (s.compared) result.compared += 1;
    const grade = gradeToneAccuracy(s.toneAccuracy);
    if (grade === 'good') result.good += 1;
    else if (grade === 'fair') result.fair += 1;
    else if (grade === 'poor') {
      result.poor += 1;
      result.worst.push(s);
    }
  });

  return result;
}
